import { useQuery } from 'data-fetching-client';
import { useDraftContext } from 'crm-index-visualization-core/core/data/VizCoreProvider';
import { getSegmentQueryInternal } from './useSegment';
import { getViewMetaQueryInternal } from './useViewMeta';
import { getVizSettingsQueryInternal } from './useVizSettings';
import { tryOr } from '../error/errorUtils';
import { getVizConfigQuery } from './viz-config-hooks';
import { convertToBackendView } from '../model/legacy-adapters';
import { Segment } from '../model/segment';
import { currentViewQuery } from './view-hooks';
import { useMemo } from 'react';
import { ViewMeta } from '../model/viewMeta';
import { ALL_VIZ_TYPES } from '../renderers/types';
const EMPTY_OBJECT = {};

/**
 * Static function to reconstruct a view from live Apollo cache data during bypass storage mode
 */
export function getSyncDraftedView(baseView, client, draftContext, clientStorage, pipelinePropertyName) {
  var _tryOr;
  const segment = (_tryOr = tryOr(null, () => {
    var _client$readQuery;
    return (_client$readQuery = client.readQuery({
      query: getSegmentQueryInternal,
      variables: {
        draftContext,
        clientStorage,
        view: baseView,
        pipelinePropertyName
      }
    })) === null || _client$readQuery === void 0 ? void 0 : _client$readQuery.vizcoreSegment;
  })) !== null && _tryOr !== void 0 ? _tryOr : Segment.fromBEView(baseView);
  const viewMeta = tryOr(null, () => {
    var _client$readQuery2;
    return (_client$readQuery2 = client.readQuery({
      query: getViewMetaQueryInternal,
      variables: {
        draftContext,
        clientStorage,
        view: baseView
      }
    })) === null || _client$readQuery2 === void 0 ? void 0 : _client$readQuery2.vizcoreViewMeta;
  });

  // Read all possible viz settings from cache
  const vizSettings = ALL_VIZ_TYPES.map(vizType => tryOr(null, () => {
    var _client$readQuery3;
    return (_client$readQuery3 = client.readQuery({
      query: getVizSettingsQueryInternal,
      variables: {
        draftContext,
        clientStorage,
        vizType,
        view: baseView
      }
    })) === null || _client$readQuery3 === void 0 ? void 0 : _client$readQuery3.vizcoreVizSettings;
  })).filter(Boolean);
  const vizConfigs = ['table', 'datawell'].map(vizType => tryOr(null, () => {
    var _client$readQuery4, _draftContext$editHas;
    return (_client$readQuery4 = client.readQuery({
      query: getVizConfigQuery,
      variables: {
        editHash: (_draftContext$editHas = draftContext.editHash) !== null && _draftContext$editHas !== void 0 ? _draftContext$editHas : '',
        clientStorage,
        vizType,
        version: '0',
        id: draftContext.viewId,
        objectTypeId: draftContext.objectTypeId
      }
    })) === null || _client$readQuery4 === void 0 ? void 0 : _client$readQuery4.vizcoreVizConfig;
  })).filter(Boolean);

  // Apply all live changes to the base view

  return [segment, viewMeta, ...vizSettings].filter(Boolean).reduce((acc, curr) => curr.toBEView(acc), convertToBackendView(segment, vizConfigs, baseView));
}

/**
 * Hook to fetch the current view based on the current view ID and object type.
 *
 * @param options.skip Whether to skip the query
 * @param options.onError Error callback
 * @param options.viewId Optional custom viewId (defaults to current viewId)
 * @param options.objectTypeId Optional custom objectTypeId (defaults to current objectTypeId)
 * @returns The Apollo query result containing the current view data
 */
export function useView(options = EMPTY_OBJECT) {
  var _options$viewId, _options$objectTypeId;
  const [{
    viewId: globalViewId,
    objectTypeId: globalObjectTypeId
  }] = useDraftContext();

  // Use provided viewId/objectTypeId or fall back to global context
  const viewId = (_options$viewId = options.viewId) !== null && _options$viewId !== void 0 ? _options$viewId : globalViewId;
  const objectTypeId = (_options$objectTypeId = options.objectTypeId) !== null && _options$objectTypeId !== void 0 ? _options$objectTypeId : globalObjectTypeId;
  const variables = useMemo(() => ({
    viewId,
    objectTypeId
  }), [viewId, objectTypeId]);
  return useQuery(currentViewQuery, {
    variables,
    skip: options.skip,
    onError: options.onError
  });
}

/**
 * Writes a view to the cache. If overwrite is true, it will overwrite the existing view in the cache.
 *
 * @param view The view to write to the cache
 * @param client The client to write the view to
 */
export function writeViewToCache(view, client, {
  overwrite = true
} = {}) {
  const viewId = ViewMeta.getIndexPageViewId(view);
  const objectTypeId = view.objectTypeId;
  const existingView = client.readQuery({
    query: currentViewQuery,
    variables: {
      viewId,
      objectTypeId
    }
  });
  if (!existingView || overwrite) {
    client.writeQuery({
      query: currentViewQuery,
      variables: {
        viewId,
        objectTypeId
      },
      data: {
        vizcoreCurrentView: view
      }
    });
  }
}